/***********************/
/* Angular + external */
import {
  Component,
  forwardRef,
  ViewChild,
  ElementRef,
  Injector,
  ChangeDetectionStrategy,
  Injectable,
} from "@angular/core";
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from "@angular/forms";

import { ObjectUtils } from "@bfx/primeng/utils";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

// Services
// Components
import { ICDBase, ICD_BASE_DEFAULT_HOST_RECORD } from "./icd-base.component";
// Models
import { ATDLInterfaceControlModel } from "../../data-atdl/model/atdl-interface-control.model";
import { ICDCheckboxModel } from "../../data-idc/controls/icd-checkbox.model";
import { ICDAuxCheckboxModel } from "../../data-idc/controls/icd-aux-checkbox.model";
// Enums - ICD Coordination
import { ICDNResponseCommand } from "../../data-idc/enums/icd-response-cmd.enum";
// Enums - ATDL
// Enums
import { ICNInputControlState } from "../controls/models/bfx-interface-controls.enums";
import { ICDNAuxMode } from "../../data-idc/enums/icd-aux-mode.enum";
import { EmphasisMixin } from "../../../../shared/base-components/mixins/emphasis";
import { InputLabelStateMixin } from "../../../../shared/base-components/mixins/label-state";
import { SizeMixin } from "../../../../shared/base-components/mixins/size";
import { BfxInputDirective } from "../../../../shared/base-components/bfx-input";

export const ICD_CHECKBOX_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => ICDCheckboxComponent),
  multi: true,
};

@Injectable()
class ICDCheckbox extends ICDBase<ICDCheckboxModel> implements ControlValueAccessor {
  /**********************************************************************/
  /* DATA */
  public config: any = {};

  public subData: any = {
    boolValue: false,
    initialValue: null,
  };

  /************************************/
  onModelChange: Function = () => {};
  onModelTouched: Function = () => {};

  /************************************/
  /* Element Ref */
  @ViewChild("inputField", { static: true }) inputField: ElementRef;
  inputDirective: BfxInputDirective;

  /**********************************************************************/
  /* CONSTRUCTOR  + LIFE CYCLE HOOKS */

  constructor(injector: Injector) {
    super(injector);
    this.oeLogger = this.oeLogger.for(__dirname);
    this.inputDirective = injector.get(BfxInputDirective);
    this.initialize();
  }

  /**
   * bind properties required by MixinDirective change detection
   */
  get bfxInput(): Record<string, any> {
    return this.inputDirective.directiveHost(this);
  }

  /**********************************************************************/
  /* INITIALIZE */

  protected initialize(): void {
    super.initialize();
    this.label = "Checkbox";
  }

  /**********************************************************************/
  /* DYNAMIC CONTROL API  */

  protected initializeICDSpecificModelProperties(): boolean {
    let error: boolean = false;
    this.cd.markForCheck();
    if (this.model instanceof ATDLInterfaceControlModel) {
      // INIT VALUE
      if (typeof this.model.initValue === "string") {
        this.data.initValue =
          this.model.initValue === "true" || this.model.initValue === "false"
            ? this.model.initValue === "true"
            : false;
        this.setAndUpdateValue(this.model.initValue);
      }
    } else {
      error = true;
    }
    return error;
  }

  /**********************************************************************/
  /* DYNAMIC CONTROL - HANDLERS */

  protected handleConstraintUpdate(data: any): void {
    this.handleDynResponses(this.processExternalStateFlowDependency(data, new Map()));
  }

  /**********************************************************************/
  /* ControlValueAccessor */

  writeValue(value: any): void {
    this.data.value = value;
    this.model.value = this.data.value;
  }

  registerOnChange(fn: Function): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: Function): void {
    this.onModelTouched = fn;
  }

  /**********************************************************************/
  /* EVENTS */

  public onFocus(event: any): void {
    this.inputFocused = true;
    this.dirty = true;
    this.data.state = ICNInputControlState.ACTIVE;
    this.data.errorMsg = "";
  }

  public onBlur(event: any): void {
    this.inputFocused = false;
    this.updateValue();
    this.validate();

    this.dynRespond(ICDNResponseCommand.UPDATE_VALUE, this.data.value);
  }

  public onChange(event: any): void {
    this.onModelChange(this.data.value);
  }

  public focusInput() {
    setTimeout(() => this.inputField.nativeElement.focus(), 50);
  }

  /**********************************************************************/
  /* EXTERNAL API */

  public setDefault(): void {
    this.setAndUpdateValue(this.data.defaultValue);
  }

  protected setAndUpdateValue(value: any): void {
    this.cd.markForCheck();
    if (value == null) {
      this.set(false);
      this.updateValue();
    } else if (value instanceof ICDCheckboxModel || typeof value === "boolean") {
      this.set(value);
      this.updateValue();
    } else if (typeof value === "string") {
      let boolValue: boolean = value === "true" || value === "false" ? value === "true" : null;
      if (boolValue != null) {
        this.set(boolValue);
        this.updateValue();
      } else {
        logger.out.error(
          `ERROR: ${this.constructor.name}.setAndUpdateValue(): Invalid string value '${value}'`,
        );
      }
    } else {
      logger.out.error(
        `ERROR: ${this.constructor.name}.setAndUpdateValue(): Invalid value %O`,
        value,
      );
    }
  }

  public updateValue(): void {
    this.cd.markForCheck();
    const newValue = this.compose();
    this.updateModel(newValue);
  }

  /**
   * Apply value change to local data, update the value in the control model, and signal change
   * @param value
   */
  protected updateModel(value: ICDCheckboxModel): void {
    if (value instanceof ICDCheckboxModel) {
      if (this.modelValueChanged(value)) {
        this.data.value = value;
        this.model.value = value;
        this.dynRespond(ICDNResponseCommand.UPDATE_VALUE, this.data.value);
        this.cd.markForCheck();
      }
    } else {
      logger.out.error("writeValue NOT ICDCheckboxModel");
    }
  }

  /**
   * Has the model value changed?
   * @param value
   * @returns
   */
  protected modelValueChanged(value: ICDCheckboxModel) {
    const oldValue: ICDCheckboxModel = this.data.value;
    if (!oldValue || !value) {
      return true;
    }
    return (
      value.value !== oldValue.value ||
      value.nulledByDependency !== oldValue.nulledByDependency ||
      value.disabled !== oldValue.disabled ||
      value.enumRef !== oldValue.enumRef
    );
  }

  public load(value: ICDCheckboxModel | boolean, propagate?: boolean): void {
    if (value instanceof ICDCheckboxModel || typeof value === "boolean" || value === null) {
      this.set(value);
      this.updateValue();
      this.validate();

      this.dynRespond(ICDNResponseCommand.LOAD_COMPLETE);

      if (typeof propagate === "boolean" && propagate) {
        this.dynRespond(ICDNResponseCommand.UPDATE_VALUE, this.data.value);
      }
    }
  }

  /**
   * The set() method is used in order to assign value to the internal(displayed) control model ('this.data.boolValue').
   *  In the case of Checkbox input the auxCheckbox is ignored.
   */
  public set(value: ICDCheckboxModel | boolean): void {
    this.cd.markForCheck();
    if (value instanceof ICDCheckboxModel || typeof value === "boolean" || value == null) {
      this.dirty = true;
      if (value === null) {
        this.data.boolValue = false;
      } else if (value instanceof ICDCheckboxModel && typeof value.value === "boolean") {
        this.data.boolValue = value.value;
      } else if (typeof value === "boolean") {
        this.data.boolValue = value;
      } else {
        logger.out.error(`ERROR: ${this.constructor.name}.set(): Invalid value type`);
      }
    }
  }

  /**
   * The compose() method is used in order to generate an outgoing model for the control (in this case ICDTextFieldModel)
   * which will be assigned to the main model 'this.data.value' and propagated to the panel.
   */
  private compose(): ICDCheckboxModel {
    let model = new ICDCheckboxModel();
    let auxModel = new ICDAuxCheckboxModel();
    auxModel.enabled = false;
    auxModel.mode = ICDNAuxMode.DISABLED;
    auxModel.value = false;
    // model
    model.nulledByDependency = this.data?.value?.nulledByDependency ?? false;
    model.disabled = this.disabled || this.data.disabled || !this.visible;
    model.value = typeof this.data.boolValue === "boolean" ? this.data.boolValue : false;
    if (
      typeof this.model.uncheckedEnumRef === "string" &&
      typeof this.model.checkedEnumRef === "string"
    ) {
      model.enumRef = this.data.boolValue ? this.model.checkedEnumRef : this.model.uncheckedEnumRef;
    } else {
      model.enumRef = null;
    }
    model.auxModel = auxModel;
    return model;
  }

  public validateIC(): boolean {
    this.cd.markForCheck();
    this.data.state = ICNInputControlState.VALID;
    this.data.errorMsg = "";
    return true;
  }

  public resetIC(): void {
    this.cd.markForCheck();
    // Internal model
    this.data.boolValue = false;
    this.data.initialValue = null;
    // Label
    this.label = "Checkbox";
  }

  /**********************************************************************/
  /* CHECKBOX */

  public onValueChange(): void {
    this.setAndUpdateValue(this.data.boolValue);
    this.validate();
    this.dynRespond(ICDNResponseCommand.UPDATE_VALUE, this.data.value);
  }
}

const Mixins = EmphasisMixin(SizeMixin(InputLabelStateMixin(ICDCheckbox)));

@Component({
  selector: "icd-checkbox",
  templateUrl: "./html/icd-checkbox.html",
  host: ICD_BASE_DEFAULT_HOST_RECORD,
  providers: [ICD_CHECKBOX_VALUE_ACCESSOR, ObjectUtils],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ICDCheckboxComponent extends Mixins {
  constructor(injector: Injector) {
    super(injector);
    this.size = "sm";
    this.emphasis = "bold";
  }
}
